// eslint-disable-next-line import/no-extraneous-dependencies
import type { DefaultOptionType } from 'rc-select/lib/Select';

import type {
  IntervalOptionValue,
  IntervalValue,
  MeasureUnitIntervalValue,
} from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalField.types';
import type { IProductSpecificationOption } from 'src/types/reference/IProductSpecificationOption';
import {
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  type SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

export function getMeasureUnitIntervalValueLabel(value: MeasureUnitIntervalValue): string {
  if (value.minValue == null) {
    return `Less than ${value.maxValue ?? '?'}${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value.measureUnit]}`;
  }

  if (value.maxValue == null) {
    return `More than ${value.minValue ?? '?'}${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value.measureUnit]}`;
  }

  return `${value.minValue} - ${value.maxValue}${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value.measureUnit]}`;
}

export function toIntervalOptionValue(value?: IntervalValue): IntervalOptionValue {
  return value != null
    ? Array.isArray(value)
      ? value.map(getMeasureUnitIntervalValueLabel)
      : getMeasureUnitIntervalValueLabel(value)
    : null;
}

export function toIntervalValue(selectedOptions?: DefaultOptionType | DefaultOptionType[] | null): IntervalValue {
  return selectedOptions != null
    ? Array.isArray(selectedOptions)
      ? selectedOptions.map((selectedOption) => ({
          measureUnit: selectedOption.measureUnit as SpecificationMeasureUnit,
          minValue: selectedOption.minValue as number,
          maxValue: selectedOption.maxValue as number,
        }))
      : {
          measureUnit: selectedOptions.measureUnit as SpecificationMeasureUnit,
          minValue: selectedOptions.minValue as number,
          maxValue: selectedOptions.maxValue as number,
        }
    : null;
}

export function toIntervalDefaultType(measureUnitOption: IProductSpecificationOption): DefaultOptionType {
  const label =
    measureUnitOption.minValue && measureUnitOption.maxValue
      ? getMeasureUnitIntervalValueLabel({
          measureUnit: measureUnitOption.measureUnit,
          minValue: measureUnitOption.minValue,
          maxValue: measureUnitOption.maxValue,
        })
      : measureUnitOption.label;

  return {
    label,
    value: label,

    // BaseType custom fields
    measureUnit: measureUnitOption.measureUnit,
    minValue: measureUnitOption.minValue,
    maxValue: measureUnitOption.maxValue,
  };
}

export function toCustomIntervalDefaultType(measureUnitValue: MeasureUnitIntervalValue): DefaultOptionType {
  const label = getMeasureUnitIntervalValueLabel(measureUnitValue);

  return {
    label: label,
    value: label,

    // BaseType custom fields
    measureUnit: measureUnitValue.measureUnit,
    minValue: measureUnitValue.minValue,
    maxValue: measureUnitValue.maxValue,

    isCustom: true,
  };
}

export function areMeasureUnitIntervalValueAndOptionEqual(
  a: MeasureUnitIntervalValue,
  b: IProductSpecificationOption
): boolean {
  return a.measureUnit === b.measureUnit && a.minValue === b.minValue && a.maxValue === b.maxValue;
}
